export type IsoDate = string;

const pad = (n: number) => (n < 10 ? `0${n}` : String(n));

/** Convert a local Date -> ISO 'YYYY-MM-DD' (local date, not UTC) */
export const toIsoLocal = (d: Date): IsoDate =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** nth weekday of month (n = 1..5), weekday: 0=Sun..6=Sat */
const nthWeekdayOfMonth = (year: number, monthZeroBased: number, weekday: number, n: number): Date => {
  const first = new Date(year, monthZeroBased, 1);
  const firstWeekday = first.getDay();
  const delta = (weekday - firstWeekday + 7) % 7;
  return new Date(year, monthZeroBased, 1 + delta + (n - 1) * 7);
};

/** last weekday of month (e.g., last Monday in May) */
const lastWeekdayOfMonth = (year: number, monthZeroBased: number, weekday: number): Date => {
  const last = new Date(year, monthZeroBased + 1, 0); // last day of month
  const delta = (last.getDay() - weekday + 7) % 7;
  return new Date(year, monthZeroBased + 1, last.getDate() - delta);
};

/**
 * Returns a Set of ISO 'YYYY-MM-DD' strings for observed US federal holidays for the given year.
 * Observance rule: if a holiday falls on Saturday -> observed on Friday; if on Sunday -> observed on Monday.
 */
export const getUSHolidaysForYear = (year: number): Set<IsoDate> => {
  const holidays: Date[] = [];

  // Fixed-date holidays
  holidays.push(new Date(year, 0, 1));   // New Year's Day
  holidays.push(new Date(year, 6, 4));   // Independence Day (July 4)
  holidays.push(new Date(year, 11, 25)); // Christmas Day (Dec 25)
  holidays.push(new Date(year, 11, 22)); // Christmas Shutdown 2025
  holidays.push(new Date(year, 11, 23)); // Christmas Shutdown 2025
  holidays.push(new Date(year, 11, 24)); // Christmas Shutdown 2025
  holidays.push(new Date(year, 11, 26)); // Christmas Shutdown 2025

  // Floating holidays
  holidays.push(lastWeekdayOfMonth(year, 4, 1));    // Memorial Day: last Mon in May
  holidays.push(nthWeekdayOfMonth(year, 8, 1, 1));  // Labor Day: 1st Mon in Sep
  holidays.push(nthWeekdayOfMonth(year, 10, 4, 4)); // Thanksgiving: 4th Thu in Nov

  const observed = new Set<IsoDate>();
  for (const d of holidays) {
    const weekday = d.getDay();
    const obs = new Date(d);
    if (weekday === 6) obs.setDate(d.getDate() - 1); // Sat -> Fri
    if (weekday === 0) obs.setDate(d.getDate() + 1); // Sun -> Mon
    observed.add(toIsoLocal(obs));
  }
  return observed;
};

/**
 * Add `businessDays` business days to `start` date skipping weekends and US federal holidays.
 * - start: Date (will not be mutated)
 * - businessDays: positive integer (0 returns a copy of start)
 * - extraHolidayIsos: optional array of ISO 'YYYY-MM-DD' strings to also treat as holidays
 *
 * Returns a new Date (local time).
 */
export const addBusinessDaysExcludingUSHolidays = (
  start: Date | string,
  businessDays: number,
  extraHolidayIsos: IsoDate[] = []
): Date => {
  if (!Number.isInteger(businessDays) || businessDays <= 0) {
    return new Date(start);
  }

  const result = new Date(start);
  const holidaySet = new Set<IsoDate>(extraHolidayIsos);

  const preloadYears = [result.getFullYear() - 1, result.getFullYear(), result.getFullYear() + 1];
  for (const y of preloadYears) {
    for (const iso of getUSHolidaysForYear(y)) holidaySet.add(iso);
  }

  let added = 0;
  while (added < businessDays) {
    result.setDate(result.getDate() + 1);
    const day = result.getDay();
    if (day === 0 || day === 6) {
      continue;
    }
    const iso = toIsoLocal(result);
    if (holidaySet.has(iso)) {
      continue;
    }
    added += 1;

    const yearStartKey = `${result.getFullYear()}-01-01`;
    if (!holidaySet.has(yearStartKey)) {
      for (const isoY of getUSHolidaysForYear(result.getFullYear())) holidaySet.add(isoY);
    }
  }

  return result;
};
